import type { BuyerSequenceRenderedDto } from "@/api/buyerSequence";
import { Button, Card } from "@/design-system/components/ui";
import { MissingRequiredNotice, UnavailableNotice } from "./CardNotices";
import { countSms, describeCharacter } from "./gsm7";
import type { MissingRequiredSummary } from "./outreachCopy";
import { OUTREACH_CARD_HEADING } from "./PulledFromCard";
import { RenderedBlock } from "./RenderedBlock";

/**
 * The Text card — the SMS the specialist pastes into their own phone.
 *
 * <p>The counter is the carrier's arithmetic, not a length: a character that
 * has no place in the GSM-7 alphabet turns the whole message into UCS-2 and
 * cuts every segment from 160 characters to 70, which is the difference
 * between one text and three. The card names the character responsible so the
 * wording can be fixed at its source (usually a curly quote or a dash pasted
 * into the template).</p>
 */
export function TextCard({
  phone,
  block,
  unavailable,
  missing,
  busy,
  copied,
  onCopyText,
  onCopyNumber,
}: {
  phone: string | null;
  /** Null when the step cannot be written — `unavailable` then says why. */
  block: BuyerSequenceRenderedDto | null;
  unavailable: string | null;
  missing: MissingRequiredSummary;
  /** The previous step's words are still on screen while the next one loads. */
  busy: boolean;
  copied: boolean;
  onCopyText: () => void;
  onCopyNumber: () => void;
}) {
  const blocked = missing.profileNouns !== null || missing.otherLabels.length > 0;
  const sms = block ? countSms(block.plain) : null;

  return (
    <Card data-testid="outreach-text-card">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <h3 className={OUTREACH_CARD_HEADING}>Text</h3>
        {sms && (
          <span className="text-caption text-gray-mid" data-testid="outreach-sms-count">
            {sms.characters} {sms.characters === 1 ? "character" : "characters"} ·{" "}
            {sms.segments} SMS {sms.segments === 1 ? "segment" : "segments"}
          </span>
        )}
      </div>

      <div className="mt-space-3 flex flex-wrap items-center justify-between gap-space-2">
        <span className="min-w-0 break-words text-small text-onyx" data-testid="outreach-text-phone">
          {phone ?? <span className="text-gray-mid">No phone on file</span>}
        </span>
        <Button
          variant="text"
          size="sm"
          onClick={onCopyNumber}
          disabled={!phone || !block || busy}
          data-testid="outreach-copy-number"
        >
          Copy number
        </Button>
      </div>

      <div className="mt-space-3 grid gap-space-3">
        {!block ? (
          <UnavailableNotice reason={unavailable ?? ""} testId="outreach-text-unavailable" />
        ) : (
          <>
            <RenderedBlock
              block={block}
              className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
              data-testid="outreach-text-body"
            />
            {sms && sms.encoding === "UCS-2" && (
              <p
                className="m-0 rounded-md bg-status-warning/15 px-space-3 py-space-2 text-small text-status-warning-ink"
                data-testid="outreach-sms-unicode"
              >
                Contains {sms.nonGsmCharacters.map(describeCharacter).join(", ")}, which a
                standard text can&apos;t carry, so it sends as Unicode: {sms.singleLimit}{" "}
                characters per text, {sms.multiLimit} each once it splits.
              </p>
            )}
            <MissingRequiredNotice summary={missing} testId="outreach-text-missing" />
          </>
        )}

        <div>
          <Button
            onClick={onCopyText}
            disabled={!block || busy || blocked}
            data-testid="outreach-copy-text"
          >
            {copied ? "Copied ✓" : "Copy text"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
